// #region CONFIG VARS
export const BILLING_CADENCE = "Quarterly";

export const BILLING_PLAN_SET_ID = "6a5f4c1cb9dfc922303a81c5";
// #endregion CONFIG VARS

// #region PLAN CARD CONFIG
const FLOW_FEATURES = [
    "nanaBO Core and Base Workers",
    "Product and image flows",
    "Order and replenishment flows",
    "Stock and sales synchronization",
    "Supported system integrations",
    "KPIs, stock and sales metrics"
];

export function buildBillingPlanCards(availableSubscriptionPlans = []) {
    return [
        buildFlowPlanCard(availableSubscriptionPlans[0]),
        buildIntelPlanCard(availableSubscriptionPlans[1]),
        buildIntelProPlanCard(availableSubscriptionPlans[2])
    ];
}

function buildFlowPlanCard(subscriptionPlan) {
    const flowPlan = getPlanRuntimeData(subscriptionPlan, "BOtag Flow");
    const activationReportsLabel = `${flowPlan.includedReports} activation reports`;

    return {
        id: "flow",
        level: 0,
        subscriptionPlanID: flowPlan.subscriptionPlanID,
        badge: "CORE",
        title: flowPlan.title,
        description: "Core BOstageR platform with unlimited stores and suppliers.",
        price: buildPlanPrice(flowPlan),
        confirmation: {
            description: `Activate your BOtag account with ${flowPlan.title}.`,
            monthlyPrice: `${flowPlan.currency} ${flowPlan.monthlyPrice}/month`,
            quarterlyBilling: `${flowPlan.currency} ${flowPlan.quarterlyPrice} billed every 3 months in advance`,
            renewal: "Auto-renews every 3 months until cancelled",
            includesTitle: "Your subscription includes:",
            includedItems: [
                "Unlimited stores and suppliers",
                "nanaBO Core and Base Workers",
                "Product, order, stock and sales data flows",
                `${activationReportsLabel} available from activation`,
                "Additional reports available for NOK 1.200 each"
            ],
            question: `Confirm ${flowPlan.title}?`
        },
        activationSuccess: {
            description: "Your payment was confirmed, and your BOtag account is now active.",
            detailsTitle: "Subscription details:",
            details: [
                `${flowPlan.currency} ${flowPlan.quarterlyPrice} paid for the next 3 months`,
                "Auto-renews every 3 months",
                "Unlimited stores and suppliers",
                `${activationReportsLabel} available`,
                "Additional reports: NOK 1.200 each"
            ],
            followUp: "You can now begin setting up your company, stores, suppliers and integrations.",
            toast: {
                title: `${flowPlan.title} activated`,
                description: `Your subscription is active, and ${activationReportsLabel} are ready to use.`
            }
        },
        allowance: {
            title: "Unlimited stores and suppliers",
            label: `${activationReportsLabel} included`,
            tooltip: {
                header: "Activation report allowance",
                title: `${activationReportsLabel} are available when your subscription starts.`
            }
        },
        sections: [
            {
                id: "flow-individual-report",
                type: "individualReport",
                prefix: "Individual reports available for",
                price: "1.200",
                currency: "NOK",
                tooltip: {
                    header: "Additional report",
                    title: "Individual reports are available for NOK 1.200 each."
                }
            },
            {
                id: "flow-features",
                type: "features",
                items: FLOW_FEATURES,
                fillAvailableSpace: true
            }
        ],
        ctaLabel: "CHOOSE FLOW"
    };
}

function buildIntelPlanCard(subscriptionPlan) {
    const intelPlan = getPlanRuntimeData(subscriptionPlan, "Buying Intel");
    return buildBuyingIntelPlanCard({
        plan: intelPlan,
        id: "buying-intel",
        level: 1,
        badge: "RECOMMENDED",
        isHighlighted: true,
        ctaLabel: "CHOOSE INTEL",
        upgradeCTALabel: "UPGRADE TO INTEL"
    });
}

function buildIntelProPlanCard(subscriptionPlan) {
    const intelProPlan = getPlanRuntimeData(subscriptionPlan, "Buying Intel Pro");
    return buildBuyingIntelPlanCard({
        plan: intelProPlan,
        id: "buying-intel-pro",
        level: 2,
        badge: "HIGH VOLUME",
        ctaLabel: "CHOOSE INTEL PRO",
        upgradeCTALabel: "UPGRADE TO INTEL PRO"
    });
}

function buildBuyingIntelPlanCard({ plan, id, level, badge, isHighlighted = false, ctaLabel, upgradeCTALabel }) {
    const quarterlyReportsLabel = `${plan.includedReports} reports per billing quarter`;

    return {
        id,
        level,
        subscriptionPlanID: plan.subscriptionPlanID,
        isHighlighted,
        badge,
        title: plan.title,
        description: `Core BOstageR platform with unlimited stores and suppliers. ${plan.includedReports} buying reports included.`,
        price: buildPlanPrice(plan),
        confirmation: {
            description: `Activate your BOtag account with ${plan.title}.`,
            monthlyPrice: `${plan.currency} ${plan.monthlyPrice}/month`,
            quarterlyBilling: `${plan.currency} ${plan.quarterlyPrice} billed every 3 months in advance`,
            renewal: "Auto-renews every 3 months until cancelled",
            includesTitle: "Your subscription includes:",
            includedItems: [
                "Unlimited stores and suppliers",
                "Everything in the BOtag Flow plan",
                quarterlyReportsLabel,
                "Quarterly report pool, not a monthly cap",
                "Reports expire at period end — no rollover",
                "Additional reports available for NOK 650 each"
            ],
            question: `Confirm ${plan.title}?`
        },
        activationSuccess: {
            description: "Your payment was confirmed, and your BOtag account is now active.",
            detailsTitle: "Subscription details:",
            details: [
                `${plan.currency} ${plan.quarterlyPrice} paid for the next 3 months`,
                "Auto-renews every 3 months",
                "Unlimited stores and suppliers",
                quarterlyReportsLabel,
                "Reports expire at period end — no rollover",
                "Additional reports: NOK 650 each"
            ],
            followUp: "You can now begin setting up your company, stores, suppliers and integrations.",
            toast: {
                title: `${plan.title} activated`,
                description: `${plan.includedReports} reports are available for the current billing quarter.`
            }
        },
        allowance: {
            title: "Unlimited stores and suppliers",
            label: `${plan.includedReports} reports included every 3 months`,
            tooltip: {
                header: "Quarterly report allowance",
                title: `${quarterlyReportsLabel} are available. Unused reports expire at the end of the period and do not roll over.`
            }
        },
        sections: [
            {
                id: `${id}-additional-report`,
                type: "individualReport",
                prefix: "Additional reports available for",
                price: "650",
                currency: "NOK",
                tooltip: {
                    header: "Additional report",
                    title: "Additional reports are available for NOK 650 each."
                }
            },
            {
                id: `${id}-features`,
                type: "features",
                items: [
                    "Everything in the BOtag Flow plan",
                    quarterlyReportsLabel,
                    "Quarterly report pool, not a monthly cap",
                    "Reports expire at period end — no rollover"
                ],
                fillAvailableSpace: true
            }
        ],
        ctaLabel,
        upgradeCTALabel
    };
}
// #endregion PLAN CARD CONFIG

// #region GLOBAL METHODS
function buildPlanPrice(plan) {
    return {
        currency: plan.currency,
        amount: plan.monthlyPrice,
        period: "/month",
        billingNote: `${plan.currency} ${plan.quarterlyPrice} billed every 3 months in advance · auto-renews`
    };
}

function getPlanRuntimeData(subscriptionPlan, fallbackTitle) {
    return {
        subscriptionPlanID: subscriptionPlan?._id || null,
        title: subscriptionPlan?.displayName || fallbackTitle,
        currency: subscriptionPlan?.currency || "NOK",
        includedReports: Number.isInteger(subscriptionPlan?.includedReports)
            ? subscriptionPlan.includedReports
            : "--",
        monthlyPrice: formatPrice(subscriptionPlan?.monthlyPrice),
        quarterlyPrice: formatPrice(subscriptionPlan?.quarterlyPrice)
    };
}

function formatPrice(price) {
    if (!Number.isFinite(price)) return "--";

    return new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(price);
}
// #endregion GLOBAL METHODS
